/**
 * GuidedTour — "A Day with Reclaim"
 *
 * This is not a product walkthrough.
 * This is a felt experience of a different way of living.
 *
 * The UI is not the focus. The feeling is the focus.
 * Screenshots fill the entire screen, dissolving in and out.
 * Text emerges from darkness. Nothing should feel like effort to watch.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Target, Shield, Music2, RotateCcw,
  SkipForward, ChevronRight, Volume2, VolumeX
} from 'lucide-react';
import { voiceVolume, getStoredAudioMode } from '../utils/modeAudio';

const API = process.env.REACT_APP_BACKEND_URL;

// Opening narration in audio runs 0-28s. We show that as silent text.
// Audio starts playing from 28s when "You wake up" begins.
const AUDIO_OFFSET = 10;

// Scenes synced EXACTLY to Whisper transcript timestamps
// Audio starts at "You wake up" (t=10s in the recording)
const SCENES = [
  { id: 'morning',       t: 10.0  },  // "You wake up... quiet sense of gratitude"
  { id: 'morning-2',     t: 43.2  },  // "less judgment, more understanding, more love"
  { id: 'immersion-1',   t: 54.0  },  // "And then you open Reclaim... step into your immersion"
  { id: 'immersion-2',   t: 61.9  },  // "settle into the reality you're creating"
  { id: 'immersion-3',   t: 69.4  },  // "something deeper is also receiving"
  { id: 'immersion-4',   t: 78.7  },  // "You're simply experiencing it"
  { id: 'challenge',     t: 87.2  },  // "drawn to your challenge for the day"
  { id: 'suite',         t: 103.3 },  // "you move into your private suite"
  { id: 'audio',         t: 108.8 },  // "you create your audio in your voice"
  { id: 'ritual',        t: 120.4 },  // "You move through your ritual"
  { id: 'becoming',      t: 128.3 },  // "You're not preparing, you're becoming"
  { id: 'shift',         t: 156.0 },  // "the way you think begins to shift"
  { id: 'reset',         t: 176.3 },  // "you pause. You return to a simple reset"
  { id: 'presence',      t: 185.0 },  // "present, steady, open"
  { id: 'balance',       t: 192.0 },  // "Tension, frustration, maybe anger"
  { id: 'release',       t: 198.3 },  // "allowing it to release"
  { id: 'alignment',     t: 205.2 },  // "close your eyes and return"
  { id: 'bank',          t: 215.2 },  // "You open your bank of reclaim"
  { id: 'connection',    t: 236.1 },  // "You connect with someone close"
  { id: 'support',       t: 243.0 },  // "this way of living extends beyond you"
  { id: 'studio',        t: 248.4 },  // before "you share something"
  { id: 'send',          t: 253.0 },  // "A message, a moment, something real"
  { id: 'evening',       t: 259.2 },  // "As the day softens"
  { id: 'night',         t: 269.3 },  // "your evening... night audio"
  { id: 'closing',       t: 290.2 },  // "naturally, effortlessly... more like you"
];

// Screenshot URLs (cropped, no borders)
const S = {
  livingVision:  `${API}/api/images/tour/living_vision.jpg`,
  vbSelect:      `${API}/api/images/tour/vb_selection_new.jpg`,
  vbCollage:     `${API}/api/images/tour/vb_collage_new.jpg`,
  breathe:       `${API}/api/images/tour/breathe_immersion.jpg`,
  challenges:    `${API}/api/images/tour/challenges.jpg`,
  chooseMode:    `${API}/api/images/tour/choose_mode.jpg`,
  ritual:        `${API}/api/images/tour/ritual.jpg`,
  resets:        `${API}/api/images/tour/resets.jpg`,
  bank:          `${API}/api/images/tour/bank.jpg`,
  voiceStudio:   `${API}/api/images/tour/voice_studio.jpg`,
  sendMoment:    `${API}/api/images/tour/send_moment.jpg`,
  alignment:     `${API}/api/images/tour/alignment.jpg`,
  spinalEase:    `${API}/api/images/tour/spinal_ease.jpg`,
  balance:       `${API}/api/images/tour/reclaim_balance.jpg`,
  healingSupport: `${API}/api/images/tour/healing_support.jpg`,
  audioPlayer:   `${API}/api/images/tour/audio_player.jpg`,
};

// ─── Full-screen screenshot (no frame, dissolves in/out) ─
const FullScreenImage = ({ src, brightness = 0.55, blur = 0, slow = false }) => (
  <motion.div className="fixed inset-0 z-5"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 2.5 }}
  >
    <motion.img
      src={src} alt=""
      className="w-full h-full object-cover"
      style={{ filter: `brightness(${brightness}) blur(${blur}px) saturate(0.85)` }}
      initial={{ scale: slow ? 1.06 : 1.03 }}
      animate={{ scale: 1 }}
      transition={{ duration: slow ? 12 : 8, ease: 'easeOut' }}
    />
    {/* Edge dissolve into darkness */}
    <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(8,6,4,0.7) 70%, rgba(8,6,4,0.95) 100%)',
    }} />
  </motion.div>
);

// ─── Text emerging from darkness ─────────────────
const EmergingText = ({ children, delay = 0, size = 'text-base md:text-lg', opacity = 0.22 }) => (
  <motion.p
    className={`${size} leading-loose text-center`}
    style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0)' }}
    animate={{ color: `rgba(255,255,255,${opacity})` }}
    transition={{ delay, duration: 3.5, ease: 'easeOut' }}
  >{children}</motion.p>
);

// ─── Feature title (Playfair Display italic) ─────
const FeatureTitle = ({ children, delay = 0.5 }) => (
  <motion.p
    className="text-sm tracking-[0.12em] mb-2"
    style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', color: 'rgba(210,175,130,0)' }}
    animate={{ color: 'rgba(210,175,130,0.25)' }}
    transition={{ delay, duration: 2.5 }}
  >{children}</motion.p>
);

// ─── Ambient glow ────────────────────────────────
const Glow = ({ color = 'rgba(180,150,100,0.03)' }) => (
  <motion.div className="absolute rounded-full pointer-events-none"
    style={{
      width: 500, height: 300, left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
      background: `radial-gradient(ellipse, ${color} 0%, transparent 60%)`, filter: 'blur(60px)',
    }}
    animate={{ scale: [1, 1.04, 1] }}
    transition={{ duration: 12, repeat: Infinity }}
  />
);

// ═══════════════════════════════════════════════════
// SCENES
// ═══════════════════════════════════════════════════

const SceneMorning = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-morning">
    <Glow color="rgba(200,170,120,0.025)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={1}>a quiet sense of gratitude…</EmergingText>
    </div>
  </div>
);

const SceneMorning2 = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-morning-2">
    <Glow color="rgba(200,170,120,0.025)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={0}>Judgement begins to soften…</EmergingText>
      <EmergingText delay={3}>Understanding starts to open…</EmergingText>
      <EmergingText delay={6}>A quiet acceptance…</EmergingText>
    </div>
  </div>
);

// Living Vision entry — FULL SCREEN
const SceneImmersion1 = () => (
  <div className="fixed inset-0" data-testid="tour-screen-immersion-1">
    <FullScreenImage src={S.livingVision} brightness={0.5} slow />
    <div className="fixed inset-0 flex items-center justify-center z-10">
      <div className="text-center">
        <FeatureTitle>Living Vision Board</FeatureTitle>
      </div>
    </div>
  </div>
);

// VB image selection — FULL SCREEN
const SceneImmersion2 = () => (
  <div className="fixed inset-0" data-testid="tour-screen-immersion-2">
    <FullScreenImage src={S.vbSelect} brightness={0.5} />
  </div>
);

// VB collage — FULL SCREEN, slow drift
const SceneImmersion3 = () => (
  <div className="fixed inset-0" data-testid="tour-screen-immersion-3">
    <FullScreenImage src={S.vbCollage} brightness={0.4} slow blur={1} />
    <div className="fixed inset-0 flex items-end justify-center z-10 pb-[25%]">
      <EmergingText delay={2} opacity={0.15}>the reality you're creating…</EmergingText>
    </div>
  </div>
);

// "Something deeper" — breathing/immersion screen
const SceneImmersion4 = () => (
  <div className="fixed inset-0" data-testid="tour-screen-immersion-4">
    <FullScreenImage src={S.breathe} brightness={0.35} blur={1.5} />
    <div className="fixed inset-0 flex items-center justify-center z-10">
      <EmergingText delay={1} opacity={0.18}>Something deeper is also receiving…</EmergingText>
    </div>
  </div>
);

// Challenge — FULL SCREEN
const SceneChallenge = () => (
  <div className="fixed inset-0" data-testid="tour-screen-challenge">
    <FullScreenImage src={S.challenges} brightness={0.45} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Challenges</FeatureTitle>
    </div>
  </div>
);

// Private Suite — FULL SCREEN
const SceneSuite = () => (
  <div className="fixed inset-0" data-testid="tour-screen-suite">
    <FullScreenImage src={S.chooseMode} brightness={0.45} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Private Suite</FeatureTitle>
    </div>
  </div>
);

// Ritual — FULL SCREEN
const SceneRitual = () => (
  <div className="fixed inset-0" data-testid="tour-screen-ritual">
    <FullScreenImage src={S.ritual} brightness={0.45} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Performance Ritual</FeatureTitle>
    </div>
  </div>
);

// "You're becoming" — pure emotional
const SceneBecoming = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-becoming">
    <Glow color="rgba(200,170,130,0.025)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={0}>You're not preparing…</EmergingText>
      <div className="h-4" />
      <EmergingText delay={3} size="text-lg md:text-xl" opacity={0.28}>You're becoming…</EmergingText>
    </div>
  </div>
);

// Shift — emotional
const SceneShift = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-shift">
    <Glow color="rgba(150,140,120,0.02)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={0}>The way you speak to yourself…</EmergingText>
      <EmergingText delay={3}>softens…</EmergingText>
    </div>
  </div>
);


// Audio listening — "you listen to your personalised audio"
const SceneAudio = () => (
  <div className="fixed inset-0" data-testid="tour-screen-audio">
    <FullScreenImage src={S.audioPlayer} brightness={0.5} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Your Audio</FeatureTitle>
    </div>
  </div>
);

// Resets — FULL SCREEN, brief
const SceneReset = () => (
  <div className="fixed inset-0" data-testid="tour-screen-reset">
    <FullScreenImage src={S.resets} brightness={0.4} blur={0.5} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Quick Resets</FeatureTitle>
    </div>
  </div>
);

// Presence — breathing
const ScenePresence = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-presence">
    <Glow color="rgba(74,103,65,0.025)" />
    <motion.div className="absolute w-28 h-28 rounded-full pointer-events-none"
      style={{ border: '1px solid rgba(74,103,65,0.06)', background: 'radial-gradient(circle, rgba(74,103,65,0.03) 0%, transparent 70%)' }}
      animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
    />
    <div className="relative z-10 text-center px-10">
      <EmergingText delay={1}>Present…</EmergingText>
      <EmergingText delay={3.5}>Steady…</EmergingText>
      <EmergingText delay={6}>Open…</EmergingText>
    </div>
  </div>
);

// Micro Journey — "tension... frustration... maybe anger" → Reclaim Our Balance
const SceneBalance = () => (
  <div className="fixed inset-0" data-testid="tour-screen-balance">
    <FullScreenImage src={S.balance} brightness={0.5} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Reclaim Our Balance</FeatureTitle>
    </div>
  </div>
);

// Release — emotional
const SceneRelease = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-release">
    <Glow color="rgba(200,160,80,0.015)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={1}>Allowing it to release…</EmergingText>
    </div>
  </div>
);

// Alignment — FULL SCREEN (real Instant Alignment screenshot)
const SceneAlignment = () => (
  <div className="fixed inset-0" data-testid="tour-screen-alignment">
    <FullScreenImage src={S.alignment} brightness={0.5} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Instant Alignment</FeatureTitle>
    </div>
  </div>
);

// Bank — FULL SCREEN, brief
const SceneBank = () => (
  <div className="fixed inset-0" data-testid="tour-screen-bank">
    <FullScreenImage src={S.bank} brightness={0.4} blur={0.5} />
    <div className="fixed inset-0 flex items-center justify-center z-10">
      <EmergingText delay={2} size="text-sm" opacity={0.18}>possible… expected… normal…</EmergingText>
    </div>
  </div>
);

// Connection — emotional
const SceneConnection = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-connection">
    <Glow color="rgba(232,160,191,0.015)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={0}>More present…</EmergingText>
      <EmergingText delay={2.5}>More patient…</EmergingText>
      <EmergingText delay={5}>More open…</EmergingText>
    </div>
  </div>
);

// Support someone — Healing Support screenshot
const SceneSupport = () => (
  <div className="fixed inset-0" data-testid="tour-screen-support">
    <FullScreenImage src={S.healingSupport} brightness={0.5} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Healing Support</FeatureTitle>
    </div>
  </div>
);

// Voice Studio — FULL SCREEN
const SceneStudio = () => (
  <div className="fixed inset-0" data-testid="tour-screen-studio">
    <FullScreenImage src={S.voiceStudio} brightness={0.45} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Voice Studio</FeatureTitle>
    </div>
  </div>
);

// Send a Moment — FULL SCREEN
const SceneSend = () => (
  <div className="fixed inset-0" data-testid="tour-screen-send">
    <FullScreenImage src={S.sendMoment} brightness={0.45} />
    <div className="fixed inset-0 flex items-start justify-center z-10 pt-[12%]">
      <FeatureTitle>Send a Moment</FeatureTitle>
    </div>
  </div>
);

// Evening — emotional
const SceneEvening = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-evening">
    <Glow color="rgba(180,150,100,0.015)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={0}>Something has shifted…</EmergingText>
      <div className="h-5" />
      <EmergingText delay={4}>Not dramatically…</EmergingText>
      <div className="h-3" />
      <EmergingText delay={7} opacity={0.28}>But deeply…</EmergingText>
    </div>
  </div>
);

// Night
const SceneNight = () => (
  <div className="fixed inset-0 flex items-center justify-center" data-testid="tour-screen-night"
    style={{ background: 'linear-gradient(180deg, rgba(10,8,18,1) 0%, rgba(6,4,12,1) 100%)' }}
  >
    <motion.div className="absolute w-[300px] h-[300px] rounded-full pointer-events-none"
      style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.015) 0%, transparent 60%)', filter: 'blur(40px)' }}
      animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 10, repeat: Infinity }}
    />
    <div className="text-center px-10 z-10">
      <EmergingText delay={1}>Without effort…</EmergingText>
    </div>
  </div>
);

// Closing
const SceneClosing = () => (
  <div className="fixed inset-0 flex flex-col items-center justify-center" data-testid="tour-screen-closing">
    <Glow color="rgba(180,150,100,0.02)" />
    <div className="text-center px-10 z-10">
      <EmergingText delay={0}>And each time you return…</EmergingText>
      <div className="h-3" />
      <EmergingText delay={3.5}>it feels more natural…</EmergingText>
      <div className="h-3" />
      <EmergingText delay={7} size="text-lg md:text-xl" opacity={0.32}>More like you…</EmergingText>
    </div>
    <motion.p className="text-xs uppercase tracking-[0.4em] mt-16 z-10"
      style={{ color: 'rgba(255,255,255,0)' }}
      animate={{ color: 'rgba(255,255,255,0.08)' }}
      transition={{ delay: 12, duration: 4 }}
    >Reclaim</motion.p>
  </div>
);

const SCREEN_MAP = {
  'morning': SceneMorning, 'morning-2': SceneMorning2,
  'immersion-1': SceneImmersion1, 'immersion-2': SceneImmersion2,
  'immersion-3': SceneImmersion3, 'immersion-4': SceneImmersion4,
  'challenge': SceneChallenge, 'suite': SceneSuite, 'ritual': SceneRitual,
  'becoming': SceneBecoming, 'shift': SceneShift, 'audio': SceneAudio,
  'reset': SceneReset, 'presence': ScenePresence, 'balance': SceneBalance, 'release': SceneRelease,
  'alignment': SceneAlignment, 'bank': SceneBank, 'connection': SceneConnection,
  'support': SceneSupport, 'studio': SceneStudio, 'send': SceneSend,
  'evening': SceneEvening, 'night': SceneNight, 'closing': SceneClosing,
};

// ─── Exploration Hub ─────────────────────────────
const ExploreScreen = ({ onNavigate }) => (
  <motion.div className="fixed inset-0 flex flex-col items-center justify-center z-10 px-6 overflow-y-auto py-12"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2.5 }}
    data-testid="tour-explore"
  >
    <motion.h2 className="text-[#F2EFE9]/50 text-xl mb-2 text-center"
      style={{ fontFamily: "'Playfair Display', serif" }}
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 2 }}
    >Explore your Reclaim</motion.h2>
    <motion.p className="text-white/12 text-xs mb-8 italic"
      style={{ fontFamily: "'Spectral', serif" }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      transition={{ delay: 1.5, duration: 1.5 }}
    >Begin wherever calls to you</motion.p>
    <div className="grid grid-cols-1 gap-2.5 w-full max-w-sm">
      {[
        { label: 'Enter Private Suite', path: '/private-suite', icon: Shield, color: '#9333ea' },
        { label: 'Start a Challenge', path: '/challenges', icon: Target, color: '#CBA153' },
        { label: 'Try an Immersion', path: '/abundance/living-vision-board', icon: Sparkles, color: '#4A6741' },
        { label: 'Create Your Audio', path: '/voice-studio', icon: Music2, color: '#5B8DEF' },
        { label: 'Explore Healing', path: '/healing', icon: RotateCcw, color: '#D4956B' },
      ].map((item, i) => (
        <motion.button key={item.path}
          onClick={() => onNavigate(item.path)}
          className="w-full px-5 py-3.5 rounded-xl bg-white/[0.02] border border-white/[0.04] flex items-center gap-4 text-left transition-all duration-300 hover:bg-white/[0.05]"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 + i * 0.15, duration: 0.6 }}
          data-testid={`tour-explore-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${item.color}10` }}>
            <item.icon size={16} style={{ color: `${item.color}60` }} />
          </div>
          <span className="text-white/40 text-sm flex-1">{item.label}</span>
          <ChevronRight size={14} className="text-white/8" />
        </motion.button>
      ))}
    </div>
  </motion.div>
);

// ═══════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════

export default function GuidedTour() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('ready');
  const [currentScene, setCurrentScene] = useState('morning');
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [introStep, setIntroStep] = useState(0);
  const audioRef = useRef(null);
  const animFrameRef = useRef(null);
  const timerStartRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Scene sync
  const startSceneSync = useCallback(() => {
    setPhase('playing');
    timerStartRef.current = Date.now();
    let lastT = -1;
    let stallCount = 0;

    const sync = () => {
      let t;
      const audio = audioRef.current;
      const audioOk = audio && !audio.paused && !audio.ended && audio.currentTime > 0;

      if (audioOk) {
        if (audio.currentTime === lastT) stallCount++;
        else { stallCount = 0; lastT = audio.currentTime; }

        if (stallCount < 60) {
          t = audio.currentTime;
          const dur = audio.duration || 253;
          setProgress(Math.min(((t - AUDIO_OFFSET) / (dur - AUDIO_OFFSET)) * 100, 100));
        } else {
          const elapsed = (Date.now() - timerStartRef.current) / 1000;
          t = AUDIO_OFFSET + elapsed;
        }
      } else {
        const elapsed = (Date.now() - timerStartRef.current) / 1000;
        t = AUDIO_OFFSET + elapsed;
        setProgress(Math.min((elapsed / (253 - AUDIO_OFFSET)) * 100, 100));
        if (elapsed >= 253 - AUDIO_OFFSET) { setPhase('explore'); return; }
      }

      let active = SCENES[0].id;
      for (let i = SCENES.length - 1; i >= 0; i--) {
        if (t >= SCENES[i].t) { active = SCENES[i].id; break; }
      }
      setCurrentScene(active);
      animFrameRef.current = requestAnimationFrame(sync);
    };
    animFrameRef.current = requestAnimationFrame(sync);
  }, []);

  // Begin button — iOS audio fix:
  // Create audio on direct click, play+pause immediately (preserves gesture),
  // then resume after text intro. User hears nothing during intro.
  const startTour = useCallback(() => {
    // Create and "unlock" audio on the direct user tap
    const audio = new Audio(`${API}/api/audio/tour/a_day_with_reclaim.mp3`);
    audioRef.current = audio;
    audio.volume = 0;
    audio.currentTime = AUDIO_OFFSET; // Pre-seek to "You wake up"
    audio.onended = () => setPhase('explore');

    // Play then immediately pause — this "unlocks" audio on iOS
    const p = audio.play();
    if (p) {
      p.then(() => {
        audio.pause(); // Immediately pause — user hears nothing
      }).catch(() => {});
    }

    // Silent text intro — matches the opening narration pace
    setPhase('text-intro');
    setIntroStep(0);
    setTimeout(() => setIntroStep(1), 2000);   // "There is another way"
    setTimeout(() => setIntroStep(2), 5500);   // "A calmer way... A more natural way"
    setTimeout(() => setIntroStep(3), 9000);   // "Immerse yourself"
    setTimeout(() => setIntroStep(4), 11000);  // "in a day with Reclaim"

    // After text intro, RESUME audio from "You wake up" (t=10s)
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.currentTime = AUDIO_OFFSET;
        audioRef.current.play().catch(() => {});
        // Fade volume up
        let vol = 0;
        const voiceTarget = voiceVolume(getStoredAudioMode());
        const fade = setInterval(() => {
          vol += 0.015;
          if (vol >= voiceTarget || !audioRef.current) { clearInterval(fade); if (audioRef.current) audioRef.current.volume = voiceTarget; }
          else if (audioRef.current) audioRef.current.volume = vol;
        }, 30);
      }
      startSceneSync();
    }, 14000);
  }, [startSceneSync]);

  const handleSkip = useCallback(() => {
    if (audioRef.current) {
      let vol = audioRef.current.volume;
      const fade = setInterval(() => {
        vol -= 0.04;
        if (vol <= 0 || !audioRef.current) { clearInterval(fade); if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }
        else if (audioRef.current) audioRef.current.volume = vol;
      }, 25);
    }
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    setPhase('explore');
  }, []);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

  const handleNavigate = useCallback((path) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    navigate(path);
  }, [navigate]);

  const ActiveScreen = SCREEN_MAP[currentScene] || SceneMorning;

  return (
    <div className="fixed inset-0 overflow-hidden" style={{ background: 'rgba(8,6,4,1)' }} data-testid="guided-tour-page">

      {/* READY */}
      {phase === 'ready' && (
        <motion.div className="fixed inset-0 flex flex-col items-center justify-center z-10"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2 }}
        >
          <Glow />
          <motion.p className="text-xs uppercase tracking-[0.35em] mb-5"
            style={{ color: 'rgba(255,255,255,0)' }}
            animate={{ color: 'rgba(255,255,255,0.15)' }}
            transition={{ delay: 0.5, duration: 2 }}
          >Reclaim</motion.p>
          <motion.p className="text-lg text-center px-12 mb-16 leading-relaxed"
            style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0)' }}
            animate={{ color: 'rgba(255,255,255,0.28)' }}
            transition={{ delay: 1.5, duration: 2.5 }}
          >A day with Reclaim</motion.p>
          <motion.button onClick={startTour}
            className="text-base tracking-widest cursor-pointer bg-transparent border-none outline-none"
            style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.18)' }}
            initial={{ opacity: 0 }} animate={{ opacity: [0, 0.6, 0.3, 0.6] }}
            transition={{ delay: 3.5, duration: 4, repeat: Infinity }}
            data-testid="tour-start-btn"
          >Begin</motion.button>
        </motion.div>
      )}

      {/* SILENT TEXT INTRO — no audio */}
      {phase === 'text-intro' && (
        <motion.div className="fixed inset-0 flex flex-col items-center justify-center z-10"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }}
        >
          <Glow />
          <div className="text-center px-10">
            <motion.p className="text-sm uppercase tracking-[0.4em] mb-10"
              style={{ color: 'rgba(255,255,255,0)' }}
              animate={{ color: 'rgba(255,255,255,0.15)' }}
              transition={{ duration: 3 }}
            >Reclaim</motion.p>

            {introStep >= 1 && (
              <motion.p className="text-lg leading-relaxed"
                style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0)' }}
                animate={{ color: 'rgba(255,255,255,0.25)' }}
                transition={{ duration: 3.5 }}
              >There is another way…</motion.p>
            )}

            {introStep >= 2 && (
              <motion.div className="mt-5">
                <motion.p className="text-base leading-loose"
                  style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0)' }}
                  animate={{ color: 'rgba(255,255,255,0.18)' }}
                  transition={{ duration: 3 }}
                >A calmer way…</motion.p>
                <motion.p className="text-base leading-loose"
                  style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0)' }}
                  animate={{ color: 'rgba(255,255,255,0.14)' }}
                  transition={{ delay: 2, duration: 3 }}
                >A more natural way…</motion.p>
              </motion.div>
            )}

            {introStep >= 3 && (
              <motion.div className="mt-8">
                <motion.p className="text-base"
                  style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0)' }}
                  animate={{ color: 'rgba(255,255,255,0.2)' }}
                  transition={{ duration: 3 }}
                >Immerse yourself…</motion.p>
                {introStep >= 4 && (
                  <motion.p className="text-base mt-2"
                    style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0)' }}
                    animate={{ color: 'rgba(255,255,255,0.22)' }}
                    transition={{ duration: 3 }}
                  >in a day with Reclaim</motion.p>
                )}
              </motion.div>
            )}
          </div>
          <button onClick={handleSkip}
            className="fixed bottom-6 right-6 flex items-center gap-2 px-3 py-1.5 text-white/[0.04] text-[10px] tracking-wider hover:text-white/10 transition-colors z-30"
            data-testid="tour-skip-btn"
          >Skip <SkipForward size={10} /></button>
        </motion.div>
      )}

      {/* PLAYING — audio-synced */}
      {phase === 'playing' && (
        <>
          <AnimatePresence mode="wait">
            <motion.div key={currentScene} className="fixed inset-0"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 2.5 }}
            >
              <ActiveScreen />
            </motion.div>
          </AnimatePresence>

          <div className="fixed bottom-0 left-0 right-0 z-30">
            <div className="w-full h-[1px] bg-white/[0.02]">
              <div className="h-full bg-white/[0.04]" style={{ width: `${progress}%`, transition: 'width 0.3s linear' }} />
            </div>
            <div className="flex items-center justify-between px-5 py-2.5">
              <button onClick={() => setMuted(m => !m)}
                className="p-2 text-white/[0.06] hover:text-white/12 transition-colors"
                data-testid="tour-mute-btn"
              >{muted ? <VolumeX size={12} /> : <Volume2 size={12} />}</button>
              <button onClick={handleSkip}
                className="flex items-center gap-2 px-3 py-1.5 text-white/[0.04] text-[10px] tracking-wider hover:text-white/10 transition-colors"
                data-testid="tour-skip-btn"
              >Skip <SkipForward size={10} /></button>
            </div>
          </div>
        </>
      )}

      {phase === 'explore' && <ExploreScreen onNavigate={handleNavigate} />}
    </div>
  );
}
